import React, { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ModalEnabled from '../../../../components/modalComponents/ModalEnabled';
import RecapField from '../../components/RecapField';
import { getRecapLabels } from '../utils/convocatoriaRecap';
import PreguntaFormModal from './PreguntaFormModal';

// ──────────────────────────────────────────────────────────────────────────
// Paso 4 — Feedback (boceto compartido por Jody, 26/08/2026).
//
// Arriba: mismo recordatorio de solo lectura que el paso 3 (RecapField).
//
// Abajo: grilla de preguntas (Pregunta / Tipo / Alternativas / Acciones) +
// botón "Nueva pregunta", que abre PreguntaFormModal como modal anidado
// (mismo patrón que "Nueva declaración" del paso 3). "Alternativas" muestra,
// separadas por comas, las alternativas asignadas a preguntas cerradas.
//
// En "Acciones", a pedido de Jody se reemplaza el ícono de eliminar (tacho)
// por un toggle activar/desactivar (visible/no visible) — corresponde a
// TP_ConvocatoriaPregunta.EsVisible. Se usa el ícono `toggle-on`/`toggle-off`
// de FontAwesome (ya disponible: App.js registra todo el set fas).
//
// Sin backend todavía: la lista de preguntas vive en el estado del wizard
// (`preguntas`/`setPreguntas` en NuevaConvocatoriaMasivo.jsx) — cuando exista
// el API se reemplaza por TP_ConvocatoriaPregunta + TP_AlternativaPregunta.
//
// El catálogo de alternativas (`alternativaOptions`) es el maestro reutilizable
// de dbo.DataType (Discriminator='AlternativasTYP', ver Script_TYP_DML.sql) del
// que se elige al configurar una pregunta cerrada. Se carga en el wizard con
// getTPAlternativasAPI y llega por prop; TP_AlternativaPregunta.IdAlternativa
// guarda el IdDataType elegido.
// ──────────────────────────────────────────────────────────────────────────

// R-09 / R-10: el formato del Director permite un maximo de 10 preguntas
// ABIERTAS y 10 CERRADAS por proceso (limites independientes, se cuentan por
// TipoPregunta). Tambien se valida server-side en ProcesoWriteUseCase (Java).
const MAX_PREGUNTAS_POR_TIPO = 10;

let nextPreguntaId = 1;

const FeedbackStep = ({ formValues, preguntas, setPreguntas, tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions, alternativaOptions = [] }) => {
    const [modalPregunta, setModalPregunta] = useState({ isEnable: false });

    const handleNuevaPregunta = () => {
        setModalPregunta({ isEnable: true, component: PreguntaFormModal, data: { catalogoAlternativas: alternativaOptions, onSave: handleAgregarPregunta } });
    }

    const handleEditarPregunta = (pregunta) => {
        setModalPregunta({ isEnable: true, component: PreguntaFormModal, data: { pregunta, catalogoAlternativas: alternativaOptions, onSave: handleGuardarEdicion } });
    }

    const contarPorTipo = (lista, tipo) => lista.filter(p => p.TipoPregunta === tipo).length;

    const handleAgregarPregunta = (nuevaPregunta) => {
        setPreguntas(lastData => {
            if (contarPorTipo(lastData, nuevaPregunta.TipoPregunta) >= MAX_PREGUNTAS_POR_TIPO) {
                alert(`Solo se permiten ${MAX_PREGUNTAS_POR_TIPO} preguntas ${nuevaPregunta.TipoPregunta === "C" ? "cerradas" : "abiertas"} por proceso.`);
                return lastData;
            }
            return [...lastData, { ...nuevaPregunta, IdTemp: nextPreguntaId++, EsVisible: true }];
        });
    }

    // Al editar se puede cambiar el tipo (A <-> C): hay que revalidar el limite
    // del tipo DESTINO, sin contar la propia pregunta que se esta editando.
    const handleGuardarEdicion = (preguntaEditada) => {
        setPreguntas(lastData => {
            const otras = lastData.filter(p => p.IdTemp !== preguntaEditada.IdTemp);
            if (contarPorTipo(otras, preguntaEditada.TipoPregunta) >= MAX_PREGUNTAS_POR_TIPO) {
                alert(`Solo se permiten ${MAX_PREGUNTAS_POR_TIPO} preguntas ${preguntaEditada.TipoPregunta === "C" ? "cerradas" : "abiertas"} por proceso.`);
                return lastData;
            }
            return lastData.map(p => p.IdTemp === preguntaEditada.IdTemp ? { ...p, ...preguntaEditada } : p);
        });
    }

    const handleToggleVisible = (pregunta) => {
        setPreguntas(lastData => lastData.map(p => p.IdTemp === pregunta.IdTemp ? { ...p, EsVisible: !p.EsVisible } : p));
    }

    const handleEliminarPregunta = (pregunta) => {
        setPreguntas(lastData => lastData.filter(p => p.IdTemp !== pregunta.IdTemp));
    }

    const { convocatoriaLabel, tipoLabel, cargoLabel, vigenciaLabel, regionesLabel, sedesLabel } =
        getRecapLabels(formValues, { tipoProcesoOptions, cargoOptions, regionOptions, sedeOptions });

    return (
        <div>
            <ModalEnabled modalEnabled={modalPregunta} setModalEnabled={setModalPregunta} />

            <div className="d-flex flex-wrap" style={{ gap: "24px" }}>
                <RecapField label="Convocatoria" value={convocatoriaLabel} />
                <RecapField label="Tipo" value={tipoLabel} />
                <RecapField label="Cargo" value={cargoLabel} />
                <RecapField label="Vigencia" value={vigenciaLabel} />
                <RecapField label="Regiones" value={regionesLabel} />
                <RecapField label="Sedes" value={sedesLabel} />
            </div>

            <hr />

            <div className="d-flex justify-content-end mb-2">
                <button type="button" className="btn generic-button-buscador" onClick={handleNuevaPregunta}>
                    Nueva pregunta (A: {contarPorTipo(preguntas, "A")}/{MAX_PREGUNTAS_POR_TIPO} · C: {contarPorTipo(preguntas, "C")}/{MAX_PREGUNTAS_POR_TIPO})
                </button>
            </div>

            <table className="table">
                <thead>
                    <tr>
                        <th>Pregunta</th>
                        <th className="text-center" style={{ width: "90px" }}>Tipo</th>
                        <th>Alternativas</th>
                        <th className="text-center" style={{ width: "120px" }}>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {preguntas.length === 0 &&
                        <tr>
                            <td colSpan={4} className="text-center text-muted">Todavía no se agregó ninguna pregunta.</td>
                        </tr>
                    }
                    {preguntas.map((pregunta) => (
                        <tr key={pregunta.IdTemp} style={{ opacity: pregunta.EsVisible ? 1 : 0.5 }}>
                            <td>{pregunta.Texto}</td>
                            <td className="text-center">{pregunta.TipoPregunta}</td>
                            <td>{pregunta.Alternativas?.map(a => a.label).join(", ")}</td>
                            <td className="text-center">
                                <div className="d-flex justify-content-center" style={{ gap: "12px" }}>
                                    <FontAwesomeIcon icon="edit" size="lg"
                                        className="hand-pointer"
                                        title="Editar"
                                        onClick={() => handleEditarPregunta(pregunta)} />
                                    <FontAwesomeIcon icon={pregunta.EsVisible ? "toggle-on" : "toggle-off"} size="lg"
                                        className="hand-pointer"
                                        style={{ color: pregunta.EsVisible ? "#8DBA38" : "#adb5bd" }}
                                        title={pregunta.EsVisible ? "Visible (clic para desactivar)" : "No visible (clic para activar)"}
                                        onClick={() => handleToggleVisible(pregunta)} />
                                    <FontAwesomeIcon icon="trash" size="lg"
                                        className="hand-pointer"
                                        title="Eliminar"
                                        onClick={() => handleEliminarPregunta(pregunta)} />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )
}

export default FeedbackStep
